import { ref, computed, watch, isRef, type Ref, type ComputedRef } from 'vue'
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BeakerIcon
} from '@heroicons/vue/24/outline'
import { groupBy } from '@/modules/Core/usings/generales'
import type { IAlertAlertas } from '@/modules/Asistencial/interfaces'
import type { IConfMaestra } from '@/modules/Configuracion/interfaces'
import { alertasService, maestraService } from '@/services'
import {
  useValidator,
  validators
} from '@/modules/Core/composables/useValidator'
import {
  type AlertGroup,
  type AlertStyle,
  type AlertCategory
} from '@/modules/Administracion/interfaces/IAlertas'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)
export interface UseAlertsOptions {
  // Accept either a raw number or a Ref<number> so callers can pass props via toRef(props,'coppId')
  coppId: number | Ref<number>
  filterByFecha?: boolean
  emit?: (event: string, payload?: unknown) => void
}

export interface UseAlertsReturn {
  // Estado
  isSaving: Ref<boolean>
  isEditing: Ref<boolean>
  alertas: Ref<IAlertAlertas[]>
  formData: Ref<IAlertAlertas>
  searchAlerta: Ref<string>
  searchServicio: Ref<string>
  alertasGrouped: Ref<AlertGroup | null>
  categoriasFiltradas: Ref<IConfMaestra[]>
  tablaTipoAlerta: Ref<IConfMaestra>

  // Constantes
  alertCategories: Record<string, AlertCategory>
  ALERT_STYLE_CONFIG: Record<string, AlertStyle>
  DEFAULT_STYLE: AlertStyle

  // Computados
  isTipoRuta: ComputedRef<boolean>
  showCancelButton: ComputedRef<boolean>
  cambioEstado: ComputedRef<boolean>

  // Métodos
  getAlertStyle: (tablaTipo: string) => AlertCategory
  getUnifiedStyle: (type: string) => AlertStyle
  getAlertNombre: (alert: IAlertAlertas) => string
  formatDate: (dateString: string) => string
  loadAlertas: () => Promise<void>
  loadCategorias: () => Promise<void>
  reloadAlertas: () => Promise<void>
  saveAlerta: (motivo?: string) => Promise<boolean>
  handleEdit: (alerta: IAlertAlertas) => Promise<void>
  resetForm: () => void
  deleteAlert: (alert: IAlertAlertas) => Promise<void>
  toggleEstado: (alert: IAlertAlertas) => Promise<void>
  handleCategoryChange: () => Promise<void>
  setupWatchers: () => void
  initialize: () => Promise<void>

  // Validación
  validateForm: () => boolean
  showError: (path: string) => string | undefined
  clearErrors: (context?: string) => void
}

// Helper puro para filtrar alertas por alerFecha. Exportado para que componentes
// puedan reutilizar la misma lógica sin acoplarse a una instancia del composable.
export function filterAlertasByFecha(
  alertas: IAlertAlertas[] | undefined | null,
  filterByFecha = true
): IAlertAlertas[] {
  const list: IAlertAlertas[] = Array.isArray(alertas) ? alertas : []
  if (!filterByFecha) return list

  const todayStr = new Date().yyyymmdd()

  return list.filter((a?: IAlertAlertas) => {
    if (!a || !a.alerFecha) return true
    try {
      const fechaStr = new Date(a.alerFecha as string).yyyymmdd()
      return fechaStr >= todayStr
    } catch {
      return true
    }
  })
}

// Divide alertas en dos arrays: activas (no vencidas o sin fecha) y activas vencidas
// Esto permite que la UI muestre por separado las "Alertas Vencidas Activas".
export function splitAlertasByFecha(
  alertas: IAlertAlertas[] | undefined | null,
  filterByFecha = true
): { active: IAlertAlertas[]; expiredActive: IAlertAlertas[], inactive: IAlertAlertas[] } {
  const list: IAlertAlertas[] = Array.isArray(alertas) ? alertas : []
  if (!filterByFecha) {
    // Todas las activas (alerEstado === 1) van a `active`, no hay vencidas si no hay filtro
    const active = list.filter(a => a && a.alerEstado === 1) as IAlertAlertas[]
    const inactive = list.filter(a => a && a.alerEstado !== 1) as IAlertAlertas[]
    return { active, expiredActive: [], inactive }
  }

  const todayStr = new Date().yyyymmdd()

  const active: IAlertAlertas[] = []
  const inactive: IAlertAlertas[] = []
  const expiredActive: IAlertAlertas[] = []

  for (const a of list) {
    if (!a) continue
    // Consideramos solo alertas activas para las listas UI solicitadas
    if (a.alerEstado !== 1) {
      inactive.push(a)
      continue
    }

    if (!a.alerFecha) {
      active.push(a)
      continue
    }

    try {
      const fechaStr = new Date(a.alerFecha as string).yyyymmdd()
      if (fechaStr >= todayStr) active.push(a)
      else expiredActive.push(a)
    } catch {
      // En caso de parseo fallido, incluir en active para no ocultar información
      active.push(a)
    }
  }

  return { active, expiredActive, inactive}
}

export function groupAlertasForDisplay(
  alertas: IAlertAlertas[] | undefined | null,
  includeExpiredActive = true
): { active: IAlertAlertas[]; inactive: IAlertAlertas[]; expiredIds: Set<number> } {
  const list = Array.isArray(alertas) ? alertas : []
  const toLocalDay = (date: Date) =>
    `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`
  const todayStr = toLocalDay(new Date())
  const active: IAlertAlertas[] = []
  const inactive: IAlertAlertas[] = []
  const expiredIds = new Set<number>()

  for (const alert of list) {
    if (!alert) continue
    if (alert.alerEstado !== 1) {
      inactive.push(alert)
      continue
    }
    if (!alert.alerFecha || !alert.alerId) {
      active.push(alert)
      continue
    }
    try {
      if (toLocalDay(new Date(alert.alerFecha as string)) < todayStr) {
        expiredIds.add(alert.alerId)
        if (includeExpiredActive) active.push(alert)
      } else {
        active.push(alert)
      }
    } catch {
      active.push(alert)
    }
  }

  return { active, inactive, expiredIds }
}

export function useAlerts(options: UseAlertsOptions): UseAlertsReturn {
  const { emit } = options
  

  // Normalizar cochaar number o Ref<number>
  // Siempre creamos un `coppId` local y escribible para evitar errores al
  // intentar asignar a un ref readonly (por ejemplo, toRef(props,'coppId')).
  const incomingCoppIdRef: Ref<number> = isRef(options.coppId)
    ? (options.coppId as Ref<number>)
    : ref(options.coppId)

  // CoppId local escribible que usará todo el composable
  const coppId: Ref<number> = ref(incomingCoppIdRef.value)

  // Mantener sincronizado el coppId local si el caller pasa un Ref
  watch(incomingCoppIdRef, (v) => {
    if (v !== undefined && v !== null) coppId.value = v
  })

  // === ESTADO REACTIVO ===
  const isSaving = ref(false)
  const isEditing = ref(false)
  const estadoOriginal = ref<number | null>(null)
  const tablaTipoAlerta = ref({}) as Ref<IConfMaestra>
  const searchAlerta = ref<string>('')
  const searchServicio = ref<string>('')
  const selectedCategoryParams = ref('')
  const alertasList = ref<IConfMaestra[] | null>(null)
  const alertasGrouped = ref<AlertGroup | null>(null)
  const previewAlert = ref({})
  const categoriasFiltradas = ref<IConfMaestra[]>([])
  const alertas = ref<IAlertAlertas[]>([])

  // Formulario
  const formData = ref({
    copeId: coppId.value,
    alerId: 0,
    alerTipoAlerta: null,
    alerTablaTipoAlerta: null,
    alerDetalle: '',
    alerEstado: 1,
    alerFecha: null,
    alerIsPrivada: 0,
    coseId: undefined,
    alerReferenciaCoseId: undefined
  } as IAlertAlertas)

  const cambioEstado = computed(() => (
    isEditing.value &&
    estadoOriginal.value !== null &&
    Number(formData.value.alerEstado) !== estadoOriginal.value
  ))

  // === CONSTANTES DE ESTILOS Y CONFIGURACIONES ===
  const alertCategories: Record<string, AlertCategory> = {
    TBL_TIPO_EVENTOSALUD: {
      color: 'purple',
      classIcon: 'ExclamationCircleIcon',
      title: 'Evento de salud publica'
    },
    TBL_TIPO_ALERTAPERSONAL: {
      color: 'teal',
      classIcon: 'ExclamationTriangleIcon',
      title: 'Antecedente Personal'
    },
    TBL_TIPORUTAS: {
      color: 'sky',
      classIcon: 'InformationCircleIcon',
      title: 'Ruta de Atención'
    },
    TBL_TIPO_ALERTALABORATORIO: {
      color: 'indigo',
      classIcon: 'BeakerIcon',
      title: 'Laboratorio'
    },
    TBL_TIPOSCIE10: {
      color: 'indigo',
      classIcon: 'ExclamationTriangleIcon',
      title: 'Diagnóstico'
    }
  }

  const ALERT_STYLE_CONFIG: Record<string, AlertStyle> = {
    TBL_TIPO_EVENTOSALUD: {
      background: 'bg-purple-100 dark:bg-purple-900/10',
      border: 'border border-purple-200 dark:border-purple-800',
      iconClass:
        'border-purple-200 bg-purple-300 text-purple-700 dark:border-purple-900 dark:bg-purple-800 dark:text-purple-400',
      textColor: 'text-purple-800 dark:text-purple-200',
      titleColor: 'text-purple-800 dark:text-purple-200',
      title: 'Alerta de Salud',
      icon: ExclamationCircleIcon,
      color: 'purple'
    },
    TBL_TIPO_ALERTAPERSONAL: {
      background: 'bg-teal-100 dark:bg-teal-900/10',
      border: 'border border-teal-200 dark:border-teal-800',
      iconClass:
        'border-teal-200 bg-teal-300 text-teal-700 dark:border-teal-900 dark:bg-teal-800 dark:text-teal-400',
      textColor: 'text-teal-800 dark:text-teal-200',
      titleColor: 'text-teal-800 dark:text-teal-200',
      title: 'Alerta Personal',
      icon: ExclamationTriangleIcon,
      color: 'teal'
    },
    TBL_TIPORUTAS: {
      background: 'bg-sky-100 dark:bg-sky-900/10',
      border: 'border border-sky-200 dark:border-sky-800',
      iconClass:
        'border-sky-200 bg-sky-300 text-sky-700 dark:border-sky-900 dark:bg-sky-800 dark:text-sky-400',
      textColor: 'text-sky-800 dark:text-sky-200',
      titleColor: 'text-sky-800 dark:text-sky-200',
      title: 'Alerta de Rutas',
      icon: InformationCircleIcon,
      color: 'sky'
    },
    TBL_TIPO_ALERTALABORATORIO: {
      background: 'bg-indigo-100 dark:bg-indigo-900/10',
      border: 'border border-indigo-200 dark:border-indigo-800',
      iconClass:
        'border-indigo-200 bg-indigo-300 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-800 dark:text-indigo-400',
      textColor: 'text-indigo-800 dark:text-indigo-200',
      titleColor: 'text-indigo-800 dark:text-indigo-200',
      title: 'Alerta de Laboratorio',
      icon: BeakerIcon,
      color: 'indigo'
    },
    TBL_TIPOSCIE10: {
      background: 'bg-indigo-100 dark:bg-indigo-900/10',
      border: 'border border-indigo-200 dark:border-indigo-800',
      iconClass:
        'border-indigo-200 bg-indigo-300 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-800 dark:text-indigo-500',
      textColor: 'text-indigo-800 dark:text-indigo-200',
      titleColor: 'text-indigo-800 dark:text-indigo-200',
      title: 'Alerta de CIE-10',
      icon: ExclamationTriangleIcon,
      color: 'indigo'
    }
  }

  const DEFAULT_STYLE = {
    background: 'bg-blue-100 dark:bg-white/10',
    border: 'border border-blue-200 dark:border-white/20',
    iconClass:
      'border-blue-200 bg-blue-300 text-blue-700 dark:border-blue-900 dark:bg-blue-800 dark:text-blue-400',
    textColor: 'text-blue-800 dark:text-blue-200',
    titleColor: 'text-blue-800 dark:text-blue-200',
    title: 'Alerta',
    icon: ExclamationCircleIcon,
    color: 'blue'
  }

  // === FUNCIONES AUXILIARES PARA CATEGORÍAS ===
  const filtrarCategorias = (categorias: IConfMaestra[]) => {
    const categoriasExcluidas = ['TBL_TIPOSCIE10', 'TBL_TIPO_ALERTAPERSONAL']
    return categorias.filter(
      cat => !categoriasExcluidas.includes(cat.comaNombrecorto)
    )
  }

  const handleCategoryChange = async () => {
    alertasGrouped.value = null
    searchAlerta.value = ''
    searchServicio.value = ''
    alertasList.value = null
    formData.value.alerTipoAlerta = null
    formData.value.coseId = undefined
    formData.value.alerReferenciaCoseId = undefined

    try {
      const { data } = await maestraService.search({
        comaId: formData.value.alerTablaTipoAlerta,
        offset: 0,
        limit: 1
      })
      tablaTipoAlerta.value = data.body.rows[0]
      if (data?.body?.rows?.[0]) {
        selectedCategoryParams.value = data.body.rows[0].comaNombrecorto
        alertasList.value = data.body.rows[0].tipos || []
        alertasGrouped.value = groupBy(
          alertasList.value,
          item => item.grupo || 'General'
        )
        const categoryStyle = getUnifiedStyle(selectedCategoryParams.value)
        previewAlert.value = {
          color: categoryStyle.color,
          classIcon: categoryStyle.iconClass
        }
      }
    } catch (error) {
      console.error('Error al obtener categoría:', error)
      selectedCategoryParams.value = ''
      alertasList.value = null
      alertasGrouped.value = null
    }
  }

  // === COMPUTADOS ===
  // Determina si la categoría seleccionada es TIPOS DE RUTA
  const isTipoRuta = computed(() => {
    const categoria = categoriasFiltradas.value.find(
      cat => cat.comaId === formData.value.alerTablaTipoAlerta
    )
    return categoria?.comaNombrecorto === 'TBL_TIPORUTAS'
  })

  const showCancelButton = computed(() => {
    return (
      isEditing.value ||
      formData.value.alerTipoAlerta !== null ||
      formData.value.alerTablaTipoAlerta !== null ||
      formData.value.alerDetalle !== ''
    )
  })

  // === VALIDACIÓN DE FORMULARIO ===
  const { validate: validateForm, showError, clearErrors, reset: resetValidator } =
    useValidator({
      formData: {
        alerTablaTipoAlerta: validators.requerido,
        alerTipoAlerta: validators.requiredIf(() => !isTipoRuta.value),
        coseId: validators.requerido
      },
      refs: { formData }
    })

  // === MÉTODOS PRINCIPALES ===
  const getAlertStyle = (tablaTipo: string) => {
    const defaultStyle = {
      color: 'blue',
      classIcon: 'ExclamationCircleIcon',
      title: 'Alerta General'
    }
    return alertCategories[tablaTipo] || defaultStyle
  }

  const getUnifiedStyle = (type: string) => {
    return ALERT_STYLE_CONFIG[type] || DEFAULT_STYLE
  }
  
  const getAlertNombre = (alert: IAlertAlertas): string => {
    return (
      alert.tipoAlerta?.comaNombrelargo ||
      alert.nombreTipoAlerta ||
      alert.servicio?.coseNombre ||
      alert.servicioReferencia?.coseNombre ||
      'Sin tipo'
    )
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // === MÉTODOS DE GESTIÓN DE ALERTAS ===
  const loadAlertas = async () => {
    try {
      const { data } = await alertasService.search({
        copeId: coppId.value
      })
      alertas.value = data.body.rows
    } catch (error) {
      console.error('Error al cargar las alertas:', error)
    }
  }

  const reloadAlertas = async () => {
    try {
      const { data } = await alertasService.search({
        copeId: coppId.value
      })
      alertas.value = data.body.rows.map((alert: IAlertAlertas) => ({
        ...alert,
        searchAlerta: alert.tipoAlerta?.comaNombrelargo
      }))
    } catch (error) {
      console.error('Error al cargar las alertas:', error)
    }
  }

  // === MÉTODOS DE GESTIÓN DE FORMULARIO ===
  const saveAlerta = async (motivo?: string) => {
    try {
      isSaving.value = true
      const isValid = validateForm()
      if (!isValid) {
        window.notificacion.notificacion(
          'failure',
          'Por favor complete todos los campos requeridos'
        )
        return false
      }
      // Cuando es TIPOS DE RUTA, copiar coseId a alerReferenciaCoseId
      if (isTipoRuta.value && formData.value.coseId) {
        formData.value.alerReferenciaCoseId = formData.value.coseId
      }
      formData.value.alerFecha = formData.value.alerFecha ? dayjs(formData.value.alerFecha).local().format('YYYY-MM-DD:HH:mm:ss') : null
      if (isEditing.value) {
        if (cambioEstado.value) {
          if (!motivo?.trim()) {
            window.notificacion.notificacion('failure', 'Debe registrar el motivo del cambio de estado')
            return false
          }
          await alertasService.update(formData.value.alerId as number, {
            ...formData.value,
            alerFechaCierre: Number(formData.value.alerEstado) === 1 ? null : formData.value.alerFechaCierre,
            motivo: motivo.trim(),
            tipoMotivo: Number(formData.value.alerEstado) === 0 ? 'CIERRE ALERTA' : 'APERTURA ALERTA'
          })
        } else {
          await alertasService.update(
            formData.value.alerId as number,
            formData.value
          )
        }
        window.notificacion.notificacion('success', 'Alerta actualizada.')
      } else {
        await alertasService.create(formData.value)
        window.notificacion.notificacion('success', 'Alerta creada.')
      }

      await reloadAlertas()
      emit?.('alerta-creada')
      emit?.('update')
      resetForm()
      return true
    } catch (error) {
      console.error('Error al actualizar el estado:', error)
      window.notificacion.notificacion('failure', 'Error al procesar la alerta')
      return false
    } finally {
      isSaving.value = false
    }
  }

  const handleEdit = async (alerta: IAlertAlertas) => {
    isEditing.value = true
    estadoOriginal.value = Number(alerta.alerEstado)
    formData.value = { ...alerta }
    formData.value.alerFecha = alerta.alerFecha ? alerta.alerFecha.split('T')[0] : null
    searchServicio.value = alerta.servicio?.coseNombre || ''
    await handleCategoryChange()
    formData.value.alerTipoAlerta = alerta.alerTipoAlerta
    searchAlerta.value = alerta.tipoAlerta?.comaNombrelargo || ''
    formData.value.coseId = alerta.alerReferenciaCoseId || alerta.coseId
  }

  const resetForm = () => {
    isEditing.value = false
    estadoOriginal.value = null
    formData.value = {
      copeId: coppId.value,
      alerId: 0,
      alerTipoAlerta: null,
      alerTablaTipoAlerta: null,
      alerDetalle: '',
      alerEstado: 1,
      alerFecha: null,
      alerIsPrivada: 0,
      coseId: undefined,
      alerReferenciaCoseId: undefined
    }
    searchAlerta.value = ''
    searchServicio.value = ''
    resetValidator()
    alertasGrouped.value = null
  }

  const deleteAlert = async (alert: IAlertAlertas) => {
    window.notificacion.confirmacion(
      'Eliminar alerta',
      '¿Estás seguro de eliminar esta alerta? Esta acción no se puede deshacer.',
      async () => {
        try {
          window.notificacion.loading().circle()
          await alertasService.delete(alert.alerId as number)
          await reloadAlertas()
          emit?.('alerta-creada')
          emit?.('update')
          window.notificacion.notificacion('success', 'Alerta eliminada.')
        } catch (error) {
          console.error('Error al eliminar la alerta:', error)
          window.notificacion.notificacion('failure', 'Error al eliminar la alerta')
        } finally {
          window.notificacion.loading().remove()
        }
      },
      () => {}
    )
  }

  const toggleEstado = async (alert: IAlertAlertas) => {
    try {
      await alertasService.update(alert.alerId as number, alert)
      await reloadAlertas()
      emit?.('alerta-creada')
      emit?.('update')
      window.notificacion.notificacion('success', 'Alerta actualizada.')
    } catch (error) {
      console.error('Error al actualizar el estado:', error)
      window.notificacion.notificacion('failure', 'Error al actualizar el estado')
    }
  }

  // === WATCHERS ===
  // Observa siempre el coppId normalizado (Ref) para recargar alertas cuando cambie.
  // Esto cubre ambos casos: caller pasó un Ref (toRef(props,'coppId')) o un número.
  const setupWatchers = () => {
    watch(coppId, async (newId, oldId) => {
      if (newId !== oldId && newId) {
        formData.value.copeId = newId
        await loadAlertas()
        resetForm()
      }
    }, { immediate: false })
  }

  // === INICIALIZACIÓN ===a
  const loadCategorias = async () => {
    const { data } = await maestraService.searchByType('TBL_ALERTAS')
    categoriasFiltradas.value = filtrarCategorias(data.body)
  }

  const initialize = async () => {
    await Promise.all([loadAlertas(), loadCategorias()])
  }

  return {
    // Estado
    isSaving,
    isEditing,
    alertas,
    formData,
    searchAlerta,
    searchServicio,
    alertasGrouped,
    categoriasFiltradas,
    tablaTipoAlerta,

    // Constantes
    alertCategories,
    ALERT_STYLE_CONFIG,
    DEFAULT_STYLE,

    // Computados
    isTipoRuta,
    showCancelButton,
    cambioEstado,

    // Métodos
    getAlertStyle,
    getUnifiedStyle,
    getAlertNombre,
    formatDate,
    loadAlertas,
    loadCategorias,
    reloadAlertas,
    saveAlerta,
    handleEdit,
    resetForm,
    deleteAlert,
    toggleEstado,
    handleCategoryChange,
    setupWatchers,
    initialize,

    // Validación
    validateForm,
    showError,
    clearErrors
  }
}
